import { observer } from 'mobx-react-lite'
import { TbFolder } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { storageTitle } from '../Helpers/diskFormat'
import { collapseDiskPath, diskPathNames, diskPathText } from '../Helpers/diskPath'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'

import type { DiskNode } from '../Contracts/DiskNode'

import styles from './Disk.module.css'

/**
 * «Де лежить» під назвою в результатах пошуку й нещодавніх: простір і папки
 * від кореня до батьківської (`ancestors` беку). Довгий шлях згортається
 * посередині («Логістика / … / 2026 / Жовтень»), а що не влізло в ширину —
 * трикрапкою в кінці; повний шлях — у підказці.
 * Клік — перехід у папку, де лежить вузол (рядок при цьому не вибирається).
 */
export const DiskNodePath = observer(({ node, onOpen }: { node: DiskNode; onOpen: (node: DiskNode) => void }) => {
    const storage = store.storageByUuid(node.storage?.uuid)
    // Чужий «Мій диск» (спільне зі мною) — не «Мій диск», а особистий диск колеги
    const rootTitle = storage
        ? storageTitle(storage)
        : node.storage
          ? node.storage.type === 'personal'
              ? 'Особистий диск'
              : node.storage.title
          : null
    const names = diskPathNames(node.ancestors, rootTitle)

    // Без шляху (старий кеш, корінь) — хоча б простір
    if (names.length === 0 && rootTitle) names.push(rootTitle)
    if (names.length === 0) return null

    const full = diskPathText(names)

    return (
        <button
            type="button"
            className={classNames(styles['path'])}
            title={`Відкрити папку: ${full}`}
            aria-label={`Відкрити папку ${full}`}
            onClick={(event) => {
                event.stopPropagation()
                onOpen(node)
            }}
            onDoubleClick={(event) => event.stopPropagation()}
            data-testid="disk-node-path"
        >
            <TbFolder className={classNames(styles['pathIcon'])} aria-hidden />
            <span className={classNames(styles['pathText'])}>
                {collapseDiskPath(names).map((name, index) => (
                    <span key={index}>
                        {index > 0 && <span className={classNames(styles['pathSep'])}>/</span>}
                        <span className={classNames(styles['pathName'])}>{name}</span>
                    </span>
                ))}
            </span>
        </button>
    )
})
